import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PrintableStatement } from "@/components/PrintableStatement";
import { StatementSection, StatementTable } from "@/pages/Farmers";
import { fmtMoney, todayStr } from "@/lib/format";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import {
  CalendarBlank,
  CaretDown,
  CaretLeft,
  CaretRight,
  CheckCircle,
  FileText,
  HandCoins,
  PencilSimple,
  Plus,
  Trash,
  UserPlus,
  UsersThree,
  Wallet,
  WhatsappLogo,
} from "@phosphor-icons/react";

import { toast } from "sonner";

const money = (value) =>
  fmtMoney(Number(value || 0));

const num = (value) =>
  Number(value || 0);

function localDateLabel(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString(
    "en-IN",
    {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    }
  );
}

function moveDate(value, days) {
  // Pure calendar-day arithmetic on the "YYYY-MM-DD" string itself — no
  // timezone-sensitive Date parsing/serialization involved, so this can't
  // drift by a day depending on the browser's local offset (which is what
  // caused "+1" to look like a no-op and "-1" to jump back two days).
  const [y, m, d] = value.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function monthYearLabel(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString(
    "en-IN",
    { month: "short", year: "numeric" }
  );
}

function seasonRangeLabel(season) {
  if (!season) return "";
  const from = monthYearLabel(season.start_date);
  const to = season.end_date
    ? monthYearLabel(season.end_date)
    : "Ongoing";
  return from === to ? from : `${from} – ${to}`;
}

/* ============================================================
   MOBILE WORKER CARD
============================================================ */

function MobileWorkerCard({
  row,
  onAdvance,
  onAccount,
  onEdit,
  onDelete,
  onSave,
  onChangeBalla,
  onChangePaid,
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">

      <button
        type="button"
        className="w-full px-4 py-3 flex items-center justify-between border-b border-slate-100 text-left"
        onClick={() => onAccount(row.worker)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
            <UsersThree size={19} className="text-slate-500" />
          </div>

          <div>
            <div className="font-semibold text-slate-800">
              {row.worker.name}
            </div>

            {row.worker.phone && (
              <div className="text-[11px] text-slate-400">
                {row.worker.phone}
              </div>
            )}
          </div>
        </div>

        <CaretRight size={18} className="text-slate-300" />
      </button>

      <div className="p-4">

        <div className="grid grid-cols-2 gap-3">

          <div>
            <Label className="text-[9px] uppercase tracking-wider text-slate-400">
              Balla
            </Label>

            <Input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              className="h-11 mt-1 text-right font-data text-base"
              placeholder="0"
              value={row.balla}
              onChange={(e) =>
                onChangeBalla(
                  row.worker.id,
                  e.target.value
                )
              }
              onBlur={(e) =>
                onSave(row, {
                  balla: e.target.value,
                })
              }
            />
          </div>

          <div>
            <Label className="text-[9px] uppercase tracking-wider text-slate-400">
              Paid Today
            </Label>

            <Input
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              className="h-11 mt-1 text-right font-data text-base"
              placeholder="0"
              value={row.paid}
              onChange={(e) =>
                onChangePaid(
                  row.worker.id,
                  e.target.value
                )
              }
              onBlur={(e) =>
                onSave(row, {
                  paid: e.target.value,
                })
              }
            />
          </div>

        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">

          <Button
            type="button"
            variant="outline"
            className="h-10 gap-1.5"
            onClick={() => onAdvance(row.worker)}
          >
            <HandCoins size={16} />
            Advance
          </Button>

          <Button
            type="button"
            variant="outline"
            className="h-10 gap-1.5"
            onClick={() => onAccount(row.worker)}
          >
            <Wallet size={16} />
            Account
          </Button>

        </div>

        {row.entry && (
          <div className="flex justify-end gap-4 mt-3 pt-2 border-t border-slate-100">

            <button
              type="button"
              onClick={() => onEdit(row)}
              className="text-xs text-slate-500 flex items-center gap-1"
            >
              <PencilSimple size={13} />
              Edit
            </button>

            <button
              type="button"
              onClick={() => onDelete(row.entry.id)}
              className="text-xs text-rose-500 flex items-center gap-1"
            >
              <Trash size={13} />
              Delete
            </button>

          </div>
        )}

      </div>
    </div>
  );
}

/* ============================================================
   WORKER ACCOUNT
============================================================ */

function WorkerAccount({
  worker,
  asOf,
  refreshKey,
  onAdvance,
  onRefresh,
  onDeleteWorker,
  onOpenStatement,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [statementMode, setStatementMode] =
    useState("day");

  const [statementDate, setStatementDate] =
    useState(asOf);

  const [fromDate, setFromDate] =
    useState(asOf);

  const [toDate, setToDate] =
    useState(asOf);

  const [statement, setStatement] =
    useState(null);

  const [statementLoading, setStatementLoading] =
    useState(false);

  const loadAccount = useCallback(
    async () => {
      try {
        setLoading(true);

        const response = await api.get(
          `/dehusk/workers/${worker.id}/account`,
          {
            params: {
              as_of: asOf,
            },
          }
        );

        setData(response.data);
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not load account"
        );
      } finally {
        setLoading(false);
      }
    },
    [worker.id, asOf, refreshKey]
  );

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const loadStatement = useCallback(
    async () => {
      try {
        setStatementLoading(true);

        const from =
          statementMode === "day"
            ? statementDate
            : fromDate;

        const to =
          statementMode === "day"
            ? statementDate
            : toDate;

        const response = await api.get(
          `/dehusk/workers/${worker.id}/statement`,
          {
            params: {
              from_date: from,
              to_date: to,
            },
          }
        );

        setStatement(response.data);
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not load work statement"
        );
      } finally {
        setStatementLoading(false);
      }
    },
    [
      worker.id,
      statementMode,
      statementDate,
      fromDate,
      toDate,
      refreshKey,
    ]
  );

  useEffect(() => {
    loadStatement();
  }, [loadStatement]);

  const settleAccount = async () => {
    if (!data) return;

    const net = num(data.net);

    const message =
      net > 0
        ? `Settle ${worker.name}'s account and pay ${money(net)}?`
        : net < 0
          ? `${worker.name} still owes ${money(Math.abs(net))}. Mark this account as settled?`
          : `Settle ${worker.name}'s account?`;

    if (!window.confirm(message)) return;

    try {
      await api.post(
        `/dehusk/workers/${worker.id}/settle`,
        {
          date: asOf,
          note: "Dehusk account settlement",
        }
      );

      toast.success("Account settled");

      await loadAccount();
      await loadStatement();
      onRefresh();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Could not settle account"
      );
    }
  };

  if (loading) {
    return (
      <div className="mt-6 text-sm text-slate-400">
        Loading account...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mt-6 text-sm text-rose-500">
        Account could not be loaded.
      </div>
    );
  }

  const net = num(data.net);

  return (
    <div className="mt-5 space-y-5">

      {/* ACCOUNT SUMMARY */}

      <div className="grid grid-cols-2 gap-2">

        <Metric
          label="Advance"
          value={money(data.total_advance)}
        />

        <Metric
          label="Interest"
          value={money(data.total_interest)}
          tone="amber"
        />

        <Metric
          label="Wage Balance"
          value={money(data.wage_balance)}
          tone="green"
        />

        <Metric
          label="Net"
          value={
            net >= 0
              ? `We owe ${money(net)}`
              : `Worker owes ${money(Math.abs(net))}`
          }
          tone={net < 0 ? "red" : "green"}
        />

      </div>

      {/* WORK STATEMENT */}

      <section className="border border-slate-200 rounded-xl p-4">

        <div className="flex items-center justify-between gap-2 mb-3">

          <div>
            <div className="font-semibold text-slate-800">
              Work Statement
            </div>

            <div className="text-[11px] text-slate-400">
              Daily and period-wise Balla history
            </div>
          </div>

          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => statement && onOpenStatement({ kind: "work", worker, statement })}
              className="gap-1"
            >
              <WhatsappLogo size={15} />
              <span className="hidden sm:inline">
                WhatsApp
              </span>
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => statement && onOpenStatement({ kind: "work", worker, statement })}
              className="gap-1"
            >
              <FileText size={15} />
              Statement
            </Button>
          </div>

        </div>

        <div className="flex gap-1 mb-3">
          <Button
            size="sm"
            variant={
              statementMode === "day"
                ? "default"
                : "outline"
            }
            onClick={() => setStatementMode("day")}
          >
            Day
          </Button>

          <Button
            size="sm"
            variant={
              statementMode === "period"
                ? "default"
                : "outline"
            }
            onClick={() => setStatementMode("period")}
          >
            Period
          </Button>
        </div>

        {statementMode === "day" ? (
          <div className="flex items-center gap-2">

            <button
              type="button"
              onClick={() =>
                setStatementDate(
                  moveDate(statementDate, -1)
                )
              }
              className="h-9 w-9 flex items-center justify-center rounded-md border border-slate-300"
            >
              <CaretLeft size={15} />
            </button>

            <Input
              type="date"
              value={statementDate}
              onChange={(e) =>
                setStatementDate(e.target.value)
              }
              className="h-9 flex-1 font-data"
            />

            <button
              type="button"
              onClick={() =>
                setStatementDate(
                  moveDate(statementDate, 1)
                )
              }
              className="h-9 w-9 flex items-center justify-center rounded-md border border-slate-300"
            >
              <CaretRight size={15} />
            </button>

          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">

            <div>
              <Label>From</Label>
              <Input
                type="date"
                className="mt-1"
                value={fromDate}
                onChange={(e) =>
                  setFromDate(e.target.value)
                }
              />
            </div>

            <div>
              <Label>To</Label>
              <Input
                type="date"
                className="mt-1"
                value={toDate}
                onChange={(e) =>
                  setToDate(e.target.value)
                }
              />
            </div>

          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-3">

          <div className="bg-slate-50 border rounded-xl p-3">
            <div className="text-[9px] uppercase text-slate-400">
              Days Worked
            </div>
            <div className="font-data text-lg font-semibold mt-1">
              {statementLoading
                ? "..."
                : statement?.summary?.days_worked ?? 0}
            </div>
          </div>

          <div className="bg-slate-50 border rounded-xl p-3">
            <div className="text-[9px] uppercase text-slate-400">
              Total Balla
            </div>
            <div className="font-data text-lg font-semibold mt-1">
              {statementLoading
                ? "..."
                : num(
                    statement?.summary?.total_balla
                  ).toFixed(2)}
            </div>
          </div>

        </div>

        <div className="space-y-2 mt-3">

          {statement?.entries?.map((entry) => (
            <div
              key={entry.id}
              className="border border-slate-200 rounded-xl p-3"
            >
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">
                  {entry.date}
                </span>

                <span className="font-data font-semibold">
                  {num(entry.balla).toFixed(2)} Balla
                </span>
              </div>

              <div className="flex justify-between mt-2 text-xs">
                <span className="text-slate-400">
                  Paid
                </span>

                <span className="font-data">
                  {money(entry.amount_paid)}
                </span>
              </div>
            </div>
          ))}

          {!statementLoading &&
            !statement?.entries?.length && (
              <div className="text-sm text-slate-400 border border-dashed rounded-xl p-5 text-center">
                No Dehusk work for this period.
              </div>
            )}

        </div>

      </section>

      {/* FINAL ACCOUNT SETTLEMENT */}

      <section className="bg-slate-900 text-white rounded-xl p-4">

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CheckCircle
              size={19}
              className="text-amber-300"
            />

            <div className="text-sm font-semibold">
              Final Settlement
            </div>
          </div>

          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onOpenStatement({ kind: "settlement", worker, data, asOf })}
              className="gap-1 bg-transparent text-white border-slate-600 hover:bg-slate-800"
            >
              <WhatsappLogo size={15} />
              <span className="hidden sm:inline">
                WhatsApp
              </span>
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => onOpenStatement({ kind: "settlement", worker, data, asOf })}
              className="gap-1 bg-transparent text-white border-slate-600 hover:bg-slate-800"
            >
              <FileText size={15} />
              Statement
            </Button>
          </div>
        </div>

        <div className="mt-4 space-y-3 font-data text-sm">

          <div className="flex justify-between">
            <span className="text-slate-400">
              Unpaid Dehusk wages
            </span>
            <span>
              {money(data.wage_balance)}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-slate-400">
              Advance
            </span>
            <span>
              - {money(data.total_advance)}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-slate-400">
              Interest
            </span>
            <span>
              - {money(data.total_interest)}
            </span>
          </div>

          <div className="border-t border-slate-700 pt-3 flex justify-between items-start">
            <span className="font-sans font-semibold pt-0.5">
              NET
            </span>

            <div className="text-right">
              <div
                className={`text-xl font-data font-semibold ${
                  net >= 0
                    ? "text-amber-300"
                    : "text-rose-300"
                }`}
              >
                {money(Math.abs(net))}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-slate-400 mt-0.5">
                {net > 0
                  ? "We owe worker"
                  : net < 0
                    ? "Worker owes us"
                    : "Settled"}
              </div>
            </div>
          </div>

        </div>

        <div className="grid grid-cols-2 gap-2 mt-5">

          <Button
            onClick={onAdvance}
            variant="outline"
            className="h-11 bg-transparent text-white border-slate-600 hover:bg-slate-800"
          >
            <HandCoins size={16} className="mr-1.5" />
            Advance
          </Button>

          <Button
            onClick={settleAccount}
            disabled={
              !data.total_advance &&
              !data.total_earned &&
              !data.total_paid
            }
            className="h-11 bg-amber-400 hover:bg-amber-500 text-slate-900"
          >
            <CheckCircle size={16} className="mr-1.5" />
            Settle
          </Button>

        </div>

      </section>

      {/* REMOVE WORKER — soft delete, history preserved */}

      {onDeleteWorker && (
        <button
          type="button"
          onClick={() => {
            const netTxt =
              net > 0
                ? ` We currently owe them ${money(net)}.`
                : net < 0
                  ? ` They currently owe us ${money(Math.abs(net))}.`
                  : "";

            if (
              !window.confirm(
                `Remove ${worker.name} from active workers?${netTxt}\n\nTheir Dehusk work history, advances and settlements will stay on record — you just won't see them in the active worker list anymore.`
              )
            ) {
              return;
            }

            onDeleteWorker();
          }}
          className="w-full flex items-center justify-center gap-2 text-xs text-rose-500 hover:text-rose-600 py-2"
        >
          <Trash size={14} />
          Remove Worker
        </button>
      )}
    </div>
  );
}

/* ============================================================
   WORKER STATEMENT OVERLAYS
   Rendered at page level, not inside the worker Sheet -- a Radix
   Sheet left open underneath keeps its focus trap / scroll lock and
   transformed container, which break the full-screen printable
   overlay (same fix as EmployeeDetail). The page closes the Sheet
   while `view` is set and reopens it on close.
============================================================ */

function WorkerStatementOverlay({ view, onClose }) {
  const { worker = {}, statement = null, data = null, asOf } = view || {};
  const safeName = (worker.name || "worker").replace(/\s+/g, "_");

  return (
    <>
      <PrintableStatement
        open={view?.kind === "work" && !!statement}
        onClose={onClose}
        testid="dehusk-statement"
        shareText={() => buildDehuskStatementShare(worker, statement)}
        pdfFileName={statement
          ? `dehusk_statement_${safeName}_${statement.period.from}_to_${statement.period.to}.pdf`
          : undefined}
      >
        {statement && <DehuskStatementBody worker={worker} statement={statement} />}
      </PrintableStatement>

      <PrintableStatement
        open={view?.kind === "settlement" && !!data}
        onClose={onClose}
        testid="dehusk-settlement-statement"
        shareText={() => buildDehuskSettlementShare(worker, data, asOf)}
        pdfFileName={`dehusk_settlement_${safeName}_${asOf}.pdf`}
      >
        {data && <DehuskSettlementBody worker={worker} data={data} asOf={asOf} />}
      </PrintableStatement>
    </>
  );
}

// Same shape as the farmer statement's share text: a short headline with
// the bottom line, the full detail travelling as the attached PDF.
function buildDehuskStatementShare(worker, statement) {
  if (!statement) return "";
  return `*Dehusk Statement — ${worker.name}*\n${statement.period.from} to ${statement.period.to}\n*${num(statement.summary.total_balla).toFixed(2)} balla · ${money(statement.summary.total_paid)} paid*\n\n📎 Full statement attached as PDF`;
}

function buildDehuskSettlementShare(worker, data, asOf) {
  if (!data) return "";
  return `*Dehusk Final Settlement — ${worker.name}*\nAs of ${asOf}\n*${dehuskNetLabel(num(data.net)).text}*\n\n📎 Full statement attached as PDF`;
}

function dehuskNetLabel(net) {
  if (net > 0) return { text: `We owe ${money(net)}`, cls: "text-emerald-700" };
  if (net < 0) return { text: `Owes us ${money(-net)}`, cls: "text-rose-700" };
  return { text: "Settled", cls: "text-slate-500" };
}

function DehuskStatementHeader({ title, worker, rightLabel, rightValue }) {
  return (
    <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-5">
      <div>
        <h1 className="font-sans text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-sm text-slate-500 mt-0.5">{worker.name}{worker.phone ? ` · ${worker.phone}` : ""}</p>
      </div>
      <div className="text-right text-xs text-slate-500">{rightLabel}<br /><span className="text-slate-800">{rightValue}</span></div>
    </div>
  );
}

function DehuskStatementBody({ worker, statement }) {
  const sm = statement.summary;
  return (
    <div className="font-data text-slate-800">
      <DehuskStatementHeader title="Dehusk Statement" worker={worker}
        rightLabel="Period" rightValue={`${statement.period.from} → ${statement.period.to}`} />

      <table className="w-full text-sm mb-6">
        <tbody>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Days worked</td><td className="py-1.5 text-right">{sm.days_worked}</td></tr>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Total balla</td><td className="py-1.5 text-right">{num(sm.total_balla).toFixed(2)}</td></tr>
          <tr className="border-t-2 border-slate-900"><td className="py-2 font-sans font-semibold">Total paid</td><td className="py-2 text-right font-semibold">{money(sm.total_paid)}</td></tr>
        </tbody>
      </table>

      <StatementSection title="Work in this period">
        <StatementTable head={["Date", "Balla", "Paid"]}
          rows={(statement.entries || []).map((e) => [e.date, num(e.balla).toFixed(2), money(e.amount_paid)])} />
      </StatementSection>

      <p className="text-[11px] text-slate-400 mt-8 font-sans">Generated by AgriLedger · {new Date().toLocaleString("en-IN")}</p>
    </div>
  );
}

function DehuskSettlementBody({ worker, data, asOf }) {
  const nl = dehuskNetLabel(num(data.net));
  return (
    <div className="font-data text-slate-800">
      <DehuskStatementHeader title="Dehusk Final Settlement" worker={worker}
        rightLabel="As of" rightValue={asOf} />

      <table className="w-full text-sm mb-6">
        <tbody>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Unpaid dehusk wages</td><td className="py-1.5 text-right">{money(data.wage_balance)}</td></tr>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Total advance</td><td className="py-1.5 text-right">{money(data.total_advance)}</td></tr>
          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-500">Accrued interest</td><td className="py-1.5 text-right">{money(data.total_interest)}</td></tr>
          <tr className="border-t-2 border-slate-900"><td className="py-2 font-sans font-semibold">Net position</td><td className={`py-2 text-right font-semibold ${nl.cls}`}>{nl.text}</td></tr>
        </tbody>
      </table>

      <StatementSection title="Advances">
        <StatementTable head={["Date", "Amount", "Rate", "Interest", "Note", ""]}
          rows={(data.advances || []).map((a) => [a.date, money(a.amount), `${a.rate}%`, money(a.accrued_interest), a.note || "—", a.settled ? "settled" : ""])} />
      </StatementSection>

      {(data.settlements || []).length > 0 && (
        <StatementSection title="Past settlements">
          <StatementTable head={["Date", "Result"]}
            rows={data.settlements.map((st) => [st.date,
              st.direction === "we_owe" ? `We paid ${money(st.net)}`
                : st.direction === "owes_us" ? `Worker paid ${money(Math.abs(st.net))}`
                : "Settled (₹0)"])} />
        </StatementSection>
      )}

      <p className="text-[11px] text-slate-400 mt-8 font-sans">Generated by AgriLedger · {new Date().toLocaleString("en-IN")}</p>
    </div>
  );
}

/* ============================================================
   DAY STATEMENT -- every worker's Balla for one date
============================================================ */

// Only entries with something recorded; a worker who was absent that day
// (no entry, or a 0 / ₹0 row left behind by clearing an input) is skipped.
function dayStatementRows(entries) {
  return entries
    .filter((e) => num(e.balla) > 0 || num(e.amount_paid) > 0)
    .slice()
    .sort((a, b) => (a.worker_name || "").localeCompare(b.worker_name || ""));
}

function dayStatementTotals(rows) {
  return rows.reduce(
    (t, e) => ({
      balla: t.balla + num(e.balla),
      earned: t.earned + num(e.total_earned),
      paid: t.paid + num(e.amount_paid),
    }),
    { balla: 0, earned: 0, paid: 0 }
  );
}

// Columns the Day Statement can show; the picker on the statement toggles
// them and the choice is remembered on this device.
const DAY_COLUMNS = [
  { key: "balla", label: "Balla", cell: (e) => num(e.balla).toFixed(2), total: (t) => t.balla.toFixed(2), share: (e) => `${num(e.balla).toFixed(2)} balla` },
  { key: "rate", label: "₹ / Balla", cell: (e) => money(e.rate_per_balla), total: () => "", share: (e) => `@ ${money(e.rate_per_balla)}` },
  { key: "earned", label: "Earned", cell: (e) => money(e.total_earned), total: (t) => money(t.earned), share: (e) => `earned ${money(e.total_earned)}` },
  { key: "paid", label: "Paid", cell: (e) => money(e.amount_paid), total: (t) => money(t.paid), share: (e) => `paid ${money(e.amount_paid)}` },
  { key: "balance", label: "Balance", cell: (e) => money(e.balance), total: (t) => money(t.earned - t.paid), share: (e) => `balance ${money(e.balance)}` },
];
const DAY_COLUMNS_DEFAULT = ["balla", "earned", "paid"];
const DAY_COLUMNS_STORE = "agri_dehusk_day_columns";

function loadDayColumns() {
  try {
    const saved = JSON.parse(localStorage.getItem(DAY_COLUMNS_STORE));
    if (Array.isArray(saved) && saved.length) return saved.filter((k) => DAY_COLUMNS.some((c) => c.key === k));
  } catch { /* fall back to defaults */ }
  return DAY_COLUMNS_DEFAULT;
}

function saveDayColumns(keys) {
  try { localStorage.setItem(DAY_COLUMNS_STORE, JSON.stringify(keys)); } catch { /* not critical */ }
}

function buildDayStatementShare(date, entries, columnKeys) {
  const rows = dayStatementRows(entries);
  const t = dayStatementTotals(rows);
  const cols = DAY_COLUMNS.filter((c) => columnKeys.includes(c.key));
  const lines = rows.map((e) => [e.worker_name, cols.map((c) => c.share(e)).join(" · ")].filter(Boolean).join(": "));
  const totals = [
    columnKeys.includes("balla") && `${t.balla.toFixed(2)} balla`,
    columnKeys.includes("earned") && `earned ${money(t.earned)}`,
    columnKeys.includes("paid") && `paid ${money(t.paid)}`,
    columnKeys.includes("balance") && `balance ${money(t.earned - t.paid)}`,
    `${rows.length} people`,
  ].filter(Boolean).join(" · ");
  return `*Dehusk Day Statement — ${localDateLabel(date)}*\n\n${lines.join("\n") || "No work recorded"}\n\n*Total: ${totals}*\n\n📎 Full statement attached as PDF`;
}

function DayColumnPicker({ columnKeys, onChange }) {
  const toggle = (key) => {
    const next = columnKeys.includes(key)
      ? columnKeys.filter((k) => k !== key)
      : DAY_COLUMNS.map((c) => c.key).filter((k) => k === key || columnKeys.includes(k));
    onChange(next);
  };

  return (
    <div className="no-print mb-5 flex flex-wrap items-center gap-2" data-html2canvas-ignore="" data-testid="day-column-picker">
      <span className="font-sans text-[10px] uppercase tracking-widest text-slate-400 font-semibold mr-1">
        Columns
      </span>
      {DAY_COLUMNS.map((c) => {
        const on = columnKeys.includes(c.key);
        return (
          <button
            key={c.key}
            type="button"
            data-testid={`day-col-${c.key}`}
            onClick={() => toggle(c.key)}
            className={`font-sans text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 ${
              on
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {on && <CheckCircle size={13} weight="fill" />}
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

function DayStatementBody({ date, entries, columnKeys, onColumnsChange }) {
  const rows = dayStatementRows(entries);
  const t = dayStatementTotals(rows);
  const cols = DAY_COLUMNS.filter((c) => columnKeys.includes(c.key));
  const metrics = [
    columnKeys.includes("balla") && <Metric key="balla" label="Total Balla" value={t.balla.toFixed(2)} />,
    columnKeys.includes("earned") && <Metric key="earned" label="Earned" value={money(t.earned)} tone="green" />,
    columnKeys.includes("paid") && <Metric key="paid" label="Paid Today" value={money(t.paid)} tone="amber" />,
    columnKeys.includes("balance") && <Metric key="balance" label="Balance" value={money(t.earned - t.paid)} />,
  ].filter(Boolean);

  return (
    <div className="font-data text-slate-800">
      <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-5">
        <div>
          <h1 className="font-sans text-2xl font-semibold tracking-tight">Dehusk Day Statement</h1>
          <p className="text-sm text-slate-500 mt-0.5">{localDateLabel(date)}</p>
        </div>
      </div>

      <DayColumnPicker columnKeys={columnKeys} onChange={onColumnsChange} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        <Metric label="People" value={rows.length} />
        {metrics}
      </div>

      <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-slate-500 mb-2">
        Workers
      </h2>

      <table className="w-full text-xs border border-slate-200" data-testid="day-statement-table">
        <thead>
          <tr className="bg-slate-100 text-slate-500 font-sans">
            <th className="px-2 py-1.5 text-left font-semibold">#</th>
            <th className="px-2 py-1.5 text-left font-semibold">Worker</th>
            {cols.map((c) => (
              <th key={c.key} className="px-2 py-1.5 text-right font-semibold">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((e, i) => (
            <tr key={e.id} className="border-t border-slate-100">
              <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
              <td className="px-2 py-1.5">{e.worker_name}</td>
              {cols.map((c) => (
                <td key={c.key} className="px-2 py-1.5 text-right">{c.cell(e)}</td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={2 + cols.length} className="px-2 py-6 text-center text-slate-400">
                No Dehusk work recorded on this day.
              </td>
            </tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-slate-900 font-semibold">
              <td className="px-2 py-2 font-sans" colSpan={2}>Day Total</td>
              {cols.map((c) => (
                <td key={c.key} className="px-2 py-2 text-right">{c.total(t)}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>

      <p className="text-[11px] text-slate-400 mt-8 font-sans">Generated by AgriLedger · {new Date().toLocaleString("en-IN")}</p>
    </div>
  );
}

/* ============================================================
   SMALL METRIC
============================================================ */

function Metric({
  label,
  value,
  tone = "default",
}) {
  const cls =
    tone === "green"
      ? "text-emerald-700"
      : tone === "amber"
        ? "text-amber-700"
        : tone === "red"
          ? "text-rose-700"
          : "text-slate-900";

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
      <div className="text-[9px] uppercase tracking-wider text-slate-400">
        {label}
      </div>

      <div className={`font-data text-lg font-semibold mt-1 ${cls}`}>
        {value}
      </div>
    </div>
  );
}

/* ============================================================
   MAIN PAGE
============================================================ */

export default function Dehusk() {
  const [date, setDate] =
    useState(todayStr());

  const [workers, setWorkers] =
    useState([]);

  const [entries, setEntries] =
    useState([]);

  const [draftEntries, setDraftEntries] =
    useState({});

  const [summary, setSummary] =
    useState({
      today: {
        balla: 0,
        workers: 0,
      },
      season: {
        balla: 0,
        start: "",
      },
      rate_per_balla: 150,
    });

  const [dailyRate, setDailyRate] =
    useState("150");

  const [rateEffectiveFrom, setRateEffectiveFrom] =
    useState(null);

  const [rateSaving, setRateSaving] =
    useState(false);

  const [workerOpen, setWorkerOpen] =
    useState(false);

  const [advanceOpen, setAdvanceOpen] =
    useState(false);

  const [editOpen, setEditOpen] =
    useState(false);

  const [statementView, setStatementView] =
    useState(null);

  const [dayStatementOpen, setDayStatementOpen] =
    useState(false);

  const [dayColumns, setDayColumns] =
    useState(loadDayColumns);

  const changeDayColumns = (keys) => {
    setDayColumns(keys);
    saveDayColumns(keys);
  };

  const [selectedWorker, setSelectedWorker] =
    useState(null);

  // Bumped after any mutation (advance, entry edit/delete, settle) so the
  // open Account Sheet's WorkerAccount re-fetches instead of showing stale
  // totals — worker/asOf alone don't change when e.g. a new advance is added
  // while the sheet is already open.
  const [accountRefreshKey, setAccountRefreshKey] =
    useState(0);
  const bumpAccountRefresh = () =>
    setAccountRefreshKey((k) => k + 1);

  const [edit, setEdit] =
    useState(null);

  const [workerForm, setWorkerForm] =
    useState({
      name: "",
      phone: "",
      note: "",
    });

  const [advance, setAdvance] =
    useState({
      amount: "",
      rate: "12",
      date: todayStr(),
      note: "",
    });

  // Guards against out-of-order responses: if the user saves an entry (which
  // triggers its own reload) and then quickly navigates to a different date,
  // the save's reload can resolve *after* the navigation's reload and
  // overwrite fresh data with stale data for the date they just left. Every
  // load() call is tagged with the date it was requested for, and results
  // are only applied if that's still the current date by the time they
  // arrive.
  const latestDateRef = useRef(date);

  useEffect(() => {
    latestDateRef.current = date;
  }, [date]);

  const load = useCallback(
    async () => {
      const requestedDate = date;

      const [
        workersResult,
        entriesResult,
        summaryResult,
        rateResult,
      ] = await Promise.allSettled([
        api.get("/dehusk/workers"),

        api.get("/dehusk/entries", {
          params: {
            date_filter: requestedDate,
          },
        }),

        api.get("/dehusk/summary", {
          params: {
            date_filter: requestedDate,
          },
        }),

        api.get("/dehusk/rate", {
          params: {
            date_filter: requestedDate,
          },
        }),
      ]);

      if (
        latestDateRef.current !==
        requestedDate
      ) {
        // A newer date has since been requested — this response is
        // stale, discard it instead of clobbering the newer data.
        return;
      }

      // Each call is applied independently now — one endpoint erroring
      // (e.g. a transient network hiccup) no longer blanks out data that
      // loaded successfully from the others. Previously this used
      // Promise.all, where a single rejection meant NONE of the four
      // results were applied — including the worker list — even though
      // the other three had already succeeded.
      if (workersResult.status === "fulfilled") {
        setWorkers(
          workersResult.value.data
        );
      } else {
        toast.error(
          workersResult.reason?.response?.data?.detail ||
            "Could not load workers"
        );
      }

      if (entriesResult.status === "fulfilled") {
        setEntries(
          entriesResult.value.data
        );
      } else {
        toast.error(
          entriesResult.reason?.response?.data?.detail ||
            "Could not load today's entries"
        );
      }

      if (summaryResult.status === "fulfilled") {
        setSummary(
          summaryResult.value.data
        );
      }

      if (rateResult.status === "fulfilled") {
        setDailyRate(
          String(
            rateResult.value.data?.rate_per_balla ??
              150
          )
        );

        setRateEffectiveFrom(
          rateResult.value.data?.effective_from ??
            null
        );
      }
    },
    [date]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Any typed-but-unsaved (not yet blurred) Balla/Paid value is local to
  // whatever date it was typed on. Without this, switching dates while a
  // draft is pending left the old value showing on the new date too, since
  // drafts were keyed only by worker id, not by (worker, date).
  useEffect(() => {
    setDraftEntries({});
  }, [date]);

  /* ============================================================
     SEASONS
  ============================================================ */

  const [seasons, setSeasons] =
    useState([]);

  const [seasonsOpen, setSeasonsOpen] =
    useState(false);

  const [newSeasonOpen, setNewSeasonOpen] =
    useState(false);

  const [seasonForm, setSeasonForm] =
    useState({
      name: "",
      start_date: todayStr(),
      default_rate_per_balla: "",
      note: "",
    });

  const [seasonSaving, setSeasonSaving] =
    useState(false);

  const [seasonReportId, setSeasonReportId] =
    useState(null);

  const [seasonReport, setSeasonReport] =
    useState(null);

  const [seasonReportLoading, setSeasonReportLoading] =
    useState(false);

  const loadSeasons = useCallback(
    async () => {
      try {
        const response = await api.get(
          "/dehusk/seasons"
        );

        setSeasons(
          response.data
        );
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not load seasons"
        );
      }
    },
    []
  );

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  const currentSeason =
    seasons.find((s) => s.is_current) || null;

  const openNewSeason = () => {
    setSeasonForm({
      name: "",
      start_date: todayStr(),
      default_rate_per_balla: dailyRate || "",
      note: "",
    });

    setNewSeasonOpen(true);
  };

  const createSeason = async () => {
    if (!seasonForm.name.trim()) {
      toast.error(
        "Season name is required"
      );
      return;
    }

    try {
      setSeasonSaving(true);

      await api.post(
        "/dehusk/seasons",
        {
          name: seasonForm.name.trim(),
          start_date: seasonForm.start_date,
          default_rate_per_balla:
            seasonForm.default_rate_per_balla === ""
              ? null
              : num(seasonForm.default_rate_per_balla),
          note: seasonForm.note,
        }
      );

      toast.success(
        `${seasonForm.name.trim()} started`
      );

      setNewSeasonOpen(false);

      await loadSeasons();
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Could not start season"
      );
    } finally {
      setSeasonSaving(false);
    }
  };

  const viewSeasonReport = async (season) => {
    setSeasonReportId(season.id);
    setSeasonReport(null);
    setSeasonReportLoading(true);

    try {
      const response = await api.get(
        `/dehusk/seasons/${season.id}/summary`
      );

      setSeasonReport(
        response.data
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Could not load season report"
      );
    } finally {
      setSeasonReportLoading(false);
    }
  };

  const shareSeasonReportText = () => {
    if (!seasonReport) return "";

    const s = seasonReport.season;

    return (
      `*Dehusk Season Report — ${s.name}*\n` +
      `${seasonReport.window_start} to ${seasonReport.window_end}\n` +
      `Total Earned: ${money(seasonReport.total_earned)}\n\n` +
      `📎 Full report attached as PDF`
    );
  };

  const workerRows =
    useMemo(() => {
      const entryMap =
        new Map(
          entries.map((entry) => [
            entry.worker_id,
            entry,
          ])
        );

      return workers.map((worker) => {
        const entry =
          entryMap.get(worker.id);

        const draft =
          draftEntries[worker.id] || {};

        return {
          worker,
          entry: entry || null,

          balla:
            draft.balla !== undefined
              ? draft.balla
              : entry?.balla ?? "",

          paid:
            draft.paid !== undefined
              ? draft.paid
              : entry?.amount_paid ?? 0,
        };
      });
    }, [
      workers,
      entries,
      draftEntries,
    ]);

  const [rateDialogOpen, setRateDialogOpen] =
    useState(false);

  const [rateForm, setRateForm] =
    useState({
      rate: "",
      effective_from: todayStr(),
    });

  const [rateHistory, setRateHistory] =
    useState([]);

  const [rateHistoryLoading, setRateHistoryLoading] =
    useState(false);

  const openRateDialog = () => {
    setRateForm({
      rate: dailyRate,
      effective_from: todayStr(),
    });

    setRateDialogOpen(true);

    setRateHistoryLoading(true);

    api.get("/dehusk/rate/history")
      .then((response) => {
        setRateHistory(
          response.data
        );
      })
      .catch(() => {
        setRateHistory([]);
      })
      .finally(() => {
        setRateHistoryLoading(false);
      });
  };

  const saveDailyRate =
    async () => {
      const value = num(rateForm.rate);

      if (value < 0) {
        toast.error(
          "Enter a valid ₹ / Balla rate"
        );
        return;
      }

      if (!rateForm.effective_from) {
        toast.error(
          "Pick an effective date"
        );
        return;
      }

      try {
        setRateSaving(true);

        await api.post(
          "/dehusk/rate",
          {
            date: rateForm.effective_from,
            rate_per_balla: value,
          }
        );

        toast.success(
          `₹/Balla set to ${value} from ${rateForm.effective_from}`
        );

        setRateDialogOpen(false);

        await load();
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not save daily rate"
        );
      } finally {
        setRateSaving(false);
      }
    };

  const saveEntry =
    async (row, patch = {}) => {
      const balla =
        patch.balla !== undefined
          ? patch.balla
          : row.balla;

      const paid =
        patch.paid !== undefined
          ? patch.paid
          : row.paid;

      if (num(balla) < 0) {
        toast.error(
          "Balla cannot be negative"
        );
        return;
      }

      if (num(paid) < 0) {
        toast.error(
          "Paid amount cannot be negative"
        );
        return;
      }

      try {
        await api.post(
          "/dehusk/entries",
          {
            worker_id:
              row.worker.id,

            date,

            balla:
              num(balla),

            amount_paid:
              num(paid),

            note:
              patch.note ??
              row.entry?.note ??
              "",
          }
        );

        setDraftEntries((old) => {
          const next = {
            ...old,
          };

          delete next[row.worker.id];

          return next;
        });

        bumpAccountRefresh();
        await load();
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not save entry"
        );
      }
    };

  const changeBalla =
    (workerId, value) => {
      setDraftEntries((old) => ({
        ...old,
        [workerId]: {
          ...old[workerId],
          balla: value,
        },
      }));
    };

  const changePaid =
    (workerId, value) => {
      setDraftEntries((old) => ({
        ...old,
        [workerId]: {
          ...old[workerId],
          paid: value,
        },
      }));
    };

  const addWorker =
    async () => {
      if (!workerForm.name.trim()) {
        toast.error(
          "Worker name is required"
        );
        return;
      }

      try {
        await api.post(
          "/dehusk/workers",
          workerForm
        );

        toast.success(
          "Person added"
        );

        setWorkerForm({
          name: "",
          phone: "",
          note: "",
        });

        setWorkerOpen(false);

        await load();
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not add person"
        );
      }
    };

  const deleteWorker =
    async (worker) => {
      if (!worker) return;

      try {
        // Soft delete — backend only flips `active: false`, so all Dehusk
        // entries/advances/settlements tied to worker.id are untouched.
        await api.delete(
          `/dehusk/workers/${worker.id}`
        );

        toast.success(
          `${worker.name} removed`
        );

        setSelectedWorker(null);

        await load();
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not remove worker"
        );
      }
    };

  const openAdvance =
    (worker) => {
      setSelectedWorker(worker);

      setAdvance({
        amount: "",
        rate: "12",
        date,
        note: "",
      });

      setAdvanceOpen(true);
    };

  const addAdvance =
    async () => {
      if (
        !selectedWorker ||
        num(advance.amount) <= 0
      ) {
        toast.error(
          "Enter an advance amount"
        );
        return;
      }

      try {
        await api.post(
          `/dehusk/workers/${selectedWorker.id}/advances`,
          {
            amount:
              num(advance.amount),

            rate:
              num(advance.rate),

            date:
              advance.date,

            note:
              advance.note,
          }
        );

        toast.success(
          `Advance recorded for ${selectedWorker.name}`
        );

        setAdvanceOpen(false);

        setAdvance({
          amount: "",
          rate: "12",
          date,
          note: "",
        });

        bumpAccountRefresh();
        await load();
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not record advance"
        );
      }
    };

  const openEdit =
    (row) => {
      if (!row.entry) return;

      setEdit({
        id:
          row.entry.id,

        worker_name:
          row.worker.name,

        balla:
          row.balla,

        amount_paid:
          row.paid,

        note:
          row.entry.note || "",
      });

      setEditOpen(true);
    };

  const updateEntry =
    async () => {
      if (!edit) return;

      try {
        await api.put(
          `/dehusk/entries/${edit.id}`,
          {
            balla:
              num(edit.balla),

            amount_paid:
              num(edit.amount_paid),

            note:
              edit.note,
          }
        );

        toast.success(
          "Entry updated"
        );

        setEditOpen(false);

        bumpAccountRefresh();
        await load();
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not update entry"
        );
      }
    };

  const deleteEntry =
    async (id) => {
      if (
        !window.confirm(
          "Delete this Dehusk entry?"
        )
      ) {
        return;
      }

      try {
        await api.delete(
          `/dehusk/entries/${id}`
        );

        toast.success(
          "Entry deleted"
        );

        bumpAccountRefresh();
        await load();
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            "Could not delete entry"
        );
      }
    };

  return (
    <div className="p-3 sm:p-4 md:p-8 max-w-[1500px] mx-auto w-full space-y-4 sm:space-y-5">

      <PageHeader
        title="Dehusk"
        subtitle="Daily Balla work, advances and account settlement."
      >
        <Button
          onClick={() =>
            setWorkerOpen(true)
          }
          className="gap-2 bg-slate-900 hover:bg-slate-800"
        >
          <UserPlus size={16} />

          <span className="hidden sm:inline">
            Add Person
          </span>

          <span className="sm:hidden">
            Add
          </span>
        </Button>
      </PageHeader>

      {/* DASHBOARD */}

      <div className="grid grid-cols-2 gap-3">

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
            Today
          </div>

          <div className="font-data text-3xl font-semibold mt-2">
            {num(
              summary.today?.balla ??
                summary.total_balla
            ).toFixed(2)}
          </div>

          <div className="text-xs text-slate-500 mt-1">
            Balla
          </div>

          <div className="text-xs text-slate-400 mt-3">
            {summary.today?.workers ??
              summary.workers ??
              0}{" "}
            people
          </div>
        </div>

        <div className="bg-slate-900 text-white rounded-xl p-4 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
            Season
          </div>

          <div className="font-data text-3xl font-semibold mt-2">
            {num(
              summary.season?.balla
            ).toFixed(2)}
          </div>

          <div className="text-xs text-slate-400 mt-1">
            Total Balla
          </div>

          {summary.season?.start && (
            <div className="text-xs text-slate-500 mt-3">
              From {summary.season.start}
            </div>
          )}
        </div>

      </div>

      {/* SEASON BAR */}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => setSeasonsOpen(true)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
            <CalendarBlank size={17} className="text-amber-600" />
          </div>

          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-wider text-slate-400">
              Season
            </div>
            <div className="font-semibold text-slate-800 truncate">
              {currentSeason
                ? currentSeason.name
                : "No active season"}
              {currentSeason && (
                <span className="font-normal text-slate-400 ml-1.5 text-xs">
                  {seasonRangeLabel(currentSeason)}
                </span>
              )}
            </div>
          </div>

          <CaretDown size={15} className="text-slate-300 shrink-0" />
        </button>

        <Button
          size="sm"
          onClick={openNewSeason}
          className="bg-slate-900 hover:bg-slate-800 gap-1.5 shrink-0"
        >
          <Plus size={15} weight="bold" />
          <span className="hidden sm:inline">New Season</span>
        </Button>
      </div>

      {/* DATE + RATE */}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3">

        <div className="flex flex-col lg:flex-row lg:items-center gap-3">

          <div className="flex items-center gap-2">

            <button
  type="button"
  onClick={() =>
    setDate(moveDate(date, -1))
  }
  className="h-10 w-10 flex items-center justify-center rounded-md border border-slate-300 bg-white hover:bg-slate-50"
>
  <CaretLeft
    size={16}
    weight="bold"
  />
</button>

<Input
  type="date"
  value={date}
  onChange={(e) =>
    setDate(e.target.value)
  }
  className="h-10 w-44 font-data"
/>

<button
  type="button"
  onClick={() =>
    setDate(moveDate(date, 1))
  }
  className="h-10 w-10 flex items-center justify-center rounded-md border border-slate-300 bg-white hover:bg-slate-50"
>
  <CaretRight
    size={16}
    weight="bold"
  />
</button>

            <div className="hidden sm:block text-sm text-slate-500">
              {localDateLabel(date)}
            </div>

          </div>

          <div className="lg:ml-auto flex flex-wrap items-center gap-3">

            <div className="text-right">
              <div className="text-[9px] uppercase tracking-wider text-slate-400">
                ₹ / Balla
              </div>
              <div className="font-data font-semibold text-slate-800">
                {money(dailyRate)}
              </div>
              <div className="text-[10px] text-slate-400">
                {rateEffectiveFrom
                  ? `since ${rateEffectiveFrom}`
                  : "default rate"}
              </div>
            </div>

            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={openRateDialog}
              className="h-10 gap-1.5"
            >
              <PencilSimple size={14} />
              Change Rate
            </Button>

            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="btn-day-statement"
              onClick={() => setDayStatementOpen(true)}
              className="h-10 gap-1.5"
            >
              <FileText size={14} />
              Day Statement
            </Button>

          </div>

        </div>

      </div>

      {/* DAILY RATE INFO */}

      <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-400">
            Today's rate
          </div>
          <div className="font-data text-lg font-semibold text-slate-800">
            {money(dailyRate)} / Balla
          </div>
        </div>

        <div className="text-right text-xs text-slate-400">
          Same rate applies to every worker on this date.
        </div>
      </div>

      {/* MOBILE WORKERS */}

      <div className="space-y-3 md:hidden">

        {workerRows.map((row) => (
          <MobileWorkerCard
            key={row.worker.id}
            row={row}
            onAdvance={
              openAdvance
            }
            onAccount={
              setSelectedWorker
            }
            onEdit={
              openEdit
            }
            onDelete={
              deleteEntry
            }
            onSave={
              saveEntry
            }
            onChangeBalla={
              changeBalla
            }
            onChangePaid={
              changePaid
            }
          />
        ))}

        {!workerRows.length && (
          <div className="text-center text-sm text-slate-400 border border-dashed rounded-xl p-8">
            No Dehusk workers added yet.
          </div>
        )}

      </div>

      {/* DESKTOP TABLE */}

      <div className="hidden md:block bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">

        <table className="w-full text-sm">

          <thead className="bg-slate-50 border-b">
            <tr className="text-[10px] uppercase tracking-wider text-slate-400">
              <th className="px-3 py-3 text-left">
                Person
              </th>

              <th className="px-3 py-3 text-right">
                Balla
              </th>

              <th className="px-3 py-3 text-right">
                Paid Today
              </th>

              <th className="px-3 py-3 text-center">
                Action
              </th>
            </tr>
          </thead>

          <tbody>

            {workerRows.map((row) => (
              <tr
                key={row.worker.id}
                className="border-b last:border-0"
              >

                <td className="px-3 py-3">

                  <button
                    type="button"
                    className="text-left"
                    onClick={() =>
                      setSelectedWorker(
                        row.worker
                      )
                    }
                  >
                    <div className="font-medium text-slate-800">
                      {row.worker.name}
                    </div>

                    {row.worker.phone && (
                      <div className="text-[10px] text-slate-400">
                        {row.worker.phone}
                      </div>
                    )}
                  </button>

                </td>

                <td className="px-3 py-3">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="h-9 w-24 ml-auto text-right font-data"
                    value={row.balla}
                    onChange={(e) =>
                      changeBalla(
                        row.worker.id,
                        e.target.value
                      )
                    }
                    onBlur={(e) =>
                      saveEntry(
                        row,
                        {
                          balla:
                            e.target.value,
                        }
                      )
                    }
                  />
                </td>

                <td className="px-3 py-3">

                  <Input
                    type="number"
                    min="0"
                    step="1"
                    className="h-9 w-28 ml-auto text-right font-data"
                    value={row.paid}
                    onChange={(e) =>
                      changePaid(
                        row.worker.id,
                        e.target.value
                      )
                    }
                    onBlur={(e) =>
                      saveEntry(
                        row,
                        {
                          paid:
                            e.target.value,
                        }
                      )
                    }
                  />

                </td>

                <td className="px-3 py-3">

                  <div className="flex justify-center items-center gap-2">

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        openAdvance(
                          row.worker
                        )
                      }
                    >
                      Advance
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setSelectedWorker(
                          row.worker
                        )
                      }
                    >
                      Account
                    </Button>

                    {row.entry && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            openEdit(row)
                          }
                          className="text-slate-500 hover:text-slate-900"
                        >
                          <PencilSimple size={16} />
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            deleteEntry(
                              row.entry.id
                            )
                          }
                          className="text-rose-500 hover:text-rose-700"
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}

                  </div>

                </td>

              </tr>
            ))}

            {!workerRows.length && (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-10 text-center text-sm text-slate-400"
                >
                  No Dehusk workers added yet. Tap "Add Person" above to add one.
                </td>
              </tr>
            )}

          </tbody>

          <tfoot>

            <tr className="bg-slate-900 text-white font-data">

              <td className="px-3 py-3 font-sans font-semibold">
                DAY TOTAL
              </td>

              <td className="px-3 py-3 text-right">
                {num(
                  summary.today?.balla ??
                    summary.total_balla
                ).toFixed(2)}
              </td>

              <td />

              <td />

            </tr>

          </tfoot>

        </table>

      </div>

      {/* ADD PERSON */}

      <Dialog
        open={workerOpen}
        onOpenChange={setWorkerOpen}
      >
        <DialogContent className="sm:max-w-[420px]">

          <DialogHeader>
            <DialogTitle>
              Add Dehusk Person
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">

            <div>
              <Label>Name</Label>
              <Input
                className="mt-1"
                value={
                  workerForm.name
                }
                onChange={(e) =>
                  setWorkerForm({
                    ...workerForm,
                    name:
                      e.target.value,
                  })
                }
                placeholder="Worker name"
              />
            </div>

            <div>
              <Label>Phone</Label>
              <Input
                className="mt-1"
                inputMode="tel"
                value={
                  workerForm.phone
                }
                onChange={(e) =>
                  setWorkerForm({
                    ...workerForm,
                    phone:
                      e.target.value,
                  })
                }
                placeholder="Optional"
              />
            </div>

            <div>
              <Label>Note</Label>
              <Input
                className="mt-1"
                value={
                  workerForm.note
                }
                onChange={(e) =>
                  setWorkerForm({
                    ...workerForm,
                    note:
                      e.target.value,
                  })
                }
                placeholder="Optional"
              />
            </div>

          </div>

          <DialogFooter>

            <Button
              variant="outline"
              onClick={() =>
                setWorkerOpen(false)
              }
            >
              Cancel
            </Button>

            <Button
              className="bg-slate-900"
              onClick={addWorker}
            >
              Add Person
            </Button>

          </DialogFooter>

        </DialogContent>
      </Dialog>

      {/* ADVANCE */}

      <Dialog
        open={advanceOpen}
        onOpenChange={
          setAdvanceOpen
        }
      >
        <DialogContent className="sm:max-w-[440px]">

          <DialogHeader>
            <DialogTitle>
              Give Advance
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">

            <div className="bg-slate-50 border rounded-lg p-3">
              <div className="text-[9px] uppercase tracking-widest text-slate-400">
                Person
              </div>

              <div className="font-semibold mt-1">
                {selectedWorker?.name}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">

              <div>
                <Label>Advance Amount</Label>
                <Input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  className="mt-1 font-data"
                  value={
                    advance.amount
                  }
                  onChange={(e) =>
                    setAdvance({
                      ...advance,
                      amount:
                        e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <Label>Annual Interest %</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 font-data"
                  value={
                    advance.rate
                  }
                  onChange={(e) =>
                    setAdvance({
                      ...advance,
                      rate:
                        e.target.value,
                    })
                  }
                />
              </div>

            </div>

            <div>
              <Label>Advance Date</Label>
              <Input
                type="date"
                className="mt-1"
                value={
                  advance.date
                }
                onChange={(e) =>
                  setAdvance({
                    ...advance,
                    date:
                      e.target.value,
                  })
                }
              />
            </div>

            <div>
              <Label>Note</Label>
              <Input
                className="mt-1"
                value={
                  advance.note
                }
                onChange={(e) =>
                  setAdvance({
                    ...advance,
                    note:
                      e.target.value,
                  })
                }
                placeholder="Before dehusking season"
              />
            </div>

            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-100 rounded-lg p-3">
              Interest is calculated from the advance date until final settlement.
              <div className="font-data mt-1 text-slate-700">
                Amount × Annual Rate × Days / 365
              </div>
            </div>

          </div>

          <DialogFooter>

            <Button
              variant="outline"
              onClick={() =>
                setAdvanceOpen(false)
              }
            >
              Cancel
            </Button>

            <Button
              className="bg-slate-900"
              onClick={addAdvance}
            >
              Record Advance
            </Button>

          </DialogFooter>

        </DialogContent>
      </Dialog>

      {/* EDIT ENTRY */}

      <Dialog
        open={editOpen}
        onOpenChange={setEditOpen}
      >
        <DialogContent className="sm:max-w-[420px]">

          <DialogHeader>
            <DialogTitle>
              Edit Dehusk Entry
            </DialogTitle>
          </DialogHeader>

          {edit && (
            <div className="space-y-4 py-2">

              <div className="font-medium text-slate-700">
                {edit.worker_name}
              </div>

              <div>
                <Label>Balla</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 font-data"
                  value={
                    edit.balla
                  }
                  onChange={(e) =>
                    setEdit({
                      ...edit,
                      balla:
                        e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <Label>Paid Today</Label>
                <Input
                  type="number"
                  min="0"
                  className="mt-1 font-data"
                  value={
                    edit.amount_paid
                  }
                  onChange={(e) =>
                    setEdit({
                      ...edit,
                      amount_paid:
                        e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <Label>Note</Label>
                <Input
                  className="mt-1"
                  value={
                    edit.note
                  }
                  onChange={(e) =>
                    setEdit({
                      ...edit,
                      note:
                        e.target.value,
                    })
                  }
                />
              </div>

              <div className="bg-slate-50 border rounded-lg p-3 text-xs text-slate-500">
                Today's ₹ / Balla is fixed at the date level:
                <span className="font-data font-semibold text-slate-700 ml-1">
                  {money(dailyRate)}
                </span>
              </div>

            </div>
          )}

          <DialogFooter>

            <Button
              variant="outline"
              onClick={() =>
                setEditOpen(false)
              }
            >
              Cancel
            </Button>

            <Button
              className="bg-slate-900"
              onClick={updateEntry}
            >
              Save Changes
            </Button>

          </DialogFooter>

        </DialogContent>
      </Dialog>

      {/* ACCOUNT SHEET */}

      <Sheet
        open={!!selectedWorker && !statementView}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedWorker(null);
          }
        }}
      >
        <SheetContent className="w-full sm:max-w-[500px] overflow-y-auto p-4 sm:p-6">

          <SheetHeader>
            <SheetTitle className="text-left">
              {selectedWorker?.name}
            </SheetTitle>
          </SheetHeader>

          {selectedWorker && (
            <WorkerAccount
              worker={selectedWorker}
              asOf={date}
              refreshKey={accountRefreshKey}
              onAdvance={() =>
                openAdvance(
                  selectedWorker
                )
              }
              onRefresh={load}
              onOpenStatement={setStatementView}
              onDeleteWorker={() =>
                deleteWorker(
                  selectedWorker
                )
              }
            />
          )}

        </SheetContent>
      </Sheet>

      <WorkerStatementOverlay
        view={statementView}
        onClose={() => setStatementView(null)}
      />

      {/* DAY STATEMENT */}

      <PrintableStatement
        open={dayStatementOpen}
        onClose={() => setDayStatementOpen(false)}
        testid="dehusk-day-statement"
        shareText={() => buildDayStatementShare(date, entries, dayColumns)}
        pdfFileName={`dehusk_day_${date}.pdf`}
      >
        {dayStatementOpen && (
          <DayStatementBody date={date} entries={entries}
            columnKeys={dayColumns} onColumnsChange={changeDayColumns} />
        )}
      </PrintableStatement>

      {/* SEASONS LIST */}

      <Sheet
        open={seasonsOpen}
        onOpenChange={setSeasonsOpen}
      >
        <SheetContent className="w-full sm:max-w-[420px] overflow-y-auto p-4 sm:p-6">

          <SheetHeader>
            <SheetTitle className="text-left">
              Seasons
            </SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-2">

            {seasons.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setSeasonsOpen(false);
                  viewSeasonReport(s);
                }}
                className="w-full text-left border border-slate-200 rounded-xl p-3 hover:bg-slate-50 flex items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate flex items-center gap-2">
                    {s.name}
                    {s.is_current && (
                      <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full shrink-0">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {s.start_date} → {s.end_date || "Ongoing"}
                  </div>
                </div>

                <FileText size={16} className="text-slate-300 shrink-0" />
              </button>
            ))}

            {seasons.length === 0 && (
              <div className="text-sm text-slate-400 border border-dashed rounded-xl p-6 text-center">
                No seasons yet. Tap "New Season" to start tracking one.
              </div>
            )}

          </div>

        </SheetContent>
      </Sheet>

      {/* NEW SEASON */}

      <Dialog
        open={newSeasonOpen}
        onOpenChange={setNewSeasonOpen}
      >
        <DialogContent className="sm:max-w-[420px]">

          <DialogHeader>
            <DialogTitle>
              Start a New Season
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">

            <div>
              <Label>Season Name</Label>
              <Input
                className="mt-1"
                placeholder="e.g. Season 2026"
                value={seasonForm.name}
                onChange={(e) =>
                  setSeasonForm((f) => ({
                    ...f,
                    name: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                className="mt-1"
                value={seasonForm.start_date}
                onChange={(e) =>
                  setSeasonForm((f) => ({
                    ...f,
                    start_date: e.target.value,
                  }))
                }
              />
              {currentSeason && (
                <p className="text-[11px] text-slate-400 mt-1">
                  "{currentSeason.name}" will be marked as ended the day before this date.
                </p>
              )}
            </div>

            <div>
              <Label>₹ / Balla (optional)</Label>
              <Input
                type="number"
                min="0"
                className="mt-1"
                placeholder="Leave blank to keep current rate"
                value={seasonForm.default_rate_per_balla}
                onChange={(e) =>
                  setSeasonForm((f) => ({
                    ...f,
                    default_rate_per_balla: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <Label>Note (optional)</Label>
              <Input
                className="mt-1"
                value={seasonForm.note}
                onChange={(e) =>
                  setSeasonForm((f) => ({
                    ...f,
                    note: e.target.value,
                  }))
                }
              />
            </div>

            <p className="text-[11px] text-slate-400">
              Advances and wage balances carry forward automatically — starting a new season never resets a worker's balance.
            </p>

          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewSeasonOpen(false)}
            >
              Cancel
            </Button>

            <Button
              onClick={createSeason}
              disabled={seasonSaving}
              className="bg-slate-900"
            >
              {seasonSaving ? "Starting..." : "Start Season"}
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>

      {/* SEASON REPORT */}

      <PrintableStatement
        open={!!seasonReportId}
        onClose={() => {
          setSeasonReportId(null);
          setSeasonReport(null);
        }}
        testid="season-report"
        shareText={shareSeasonReportText}
        pdfFileName={seasonReport
          ? `dehusk_season_${seasonReport.season.name.replace(/\s+/g, "_")}_${seasonReport.window_start}_to_${seasonReport.window_end}.pdf`
          : undefined}
      >
        {seasonReportLoading && (
          <div className="text-sm text-slate-400 py-8 text-center">
            Loading season report...
          </div>
        )}

        {seasonReport && !seasonReportLoading && (
          <div className="font-data text-slate-800">

            <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-5">
              <div>
                <h1 className="font-sans text-2xl font-semibold tracking-tight">
                  Season Report
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  {seasonReport.season.name}
                </p>
              </div>
              <div className="text-right text-xs text-slate-500">
                Period<br />
                <span className="text-slate-800">
                  {seasonReport.window_start} → {seasonReport.window_end}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
              <Metric
                label="Workers"
                value={seasonReport.total_workers}
              />

              <Metric
                label="Total Balla"
                value={num(seasonReport.total_balla).toFixed(2)}
              />

              <Metric
                label="Total Earned"
                value={money(seasonReport.total_earned)}
                tone="green"
              />

              <Metric
                label="Advances Given"
                value={money(seasonReport.total_advances_given)}
                tone="amber"
              />
            </div>

            <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-slate-500 mb-2">
              Per-Worker Breakdown
            </h2>

            <table className="w-full text-xs border border-slate-200 mb-5">
              <thead>
                <tr className="bg-slate-100 text-slate-500 font-sans">
                  <th className="px-2 py-1.5 text-left font-semibold">Worker</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Days</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Balla</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Earned</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Advances</th>
                </tr>
              </thead>
              <tbody>
                {seasonReport.rows.map((r) => (
                  <tr
                    key={r.worker_id}
                    className="border-t border-slate-100"
                  >
                    <td className="px-2 py-1.5">
                      {r.worker_name}
                      {!r.worker_active && (
                        <span className="text-[9px] text-slate-400 ml-1">
                          (removed)
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">{r.days_worked}</td>
                    <td className="px-2 py-1.5 text-right">{num(r.total_balla).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right">{money(r.total_earned)}</td>
                    <td className="px-2 py-1.5 text-right">{money(r.advances_given_in_season)}</td>
                  </tr>
                ))}

                {seasonReport.rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-2 py-6 text-center text-slate-400">
                      No Dehusk activity recorded in this season yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <p className="text-[11px] text-slate-400 mt-8 font-sans">
              Generated by AgriLedger · {new Date().toLocaleString("en-IN")}
            </p>

          </div>
        )}
      </PrintableStatement>

      {/* CHANGE ₹/BALLA RATE */}

      <Dialog
        open={rateDialogOpen}
        onOpenChange={setRateDialogOpen}
      >
        <DialogContent className="sm:max-w-[440px]">

          <DialogHeader>
            <DialogTitle>
              Change ₹ / Balla Rate
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">

            <p className="text-xs text-slate-400">
              The rate holds steady for the whole season — set it once and it applies to every day going forward. Use "Effective From" only if the rate genuinely changes partway through the season; everything before that date keeps the old rate, everything from that date on uses the new one.
            </p>

            <div>
              <Label>New Rate (₹ / Balla)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                className="mt-1 font-data"
                value={rateForm.rate}
                onChange={(e) =>
                  setRateForm((f) => ({
                    ...f,
                    rate: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <Label>Effective From</Label>
              <Input
                type="date"
                className="mt-1 font-data"
                value={rateForm.effective_from}
                onChange={(e) =>
                  setRateForm((f) => ({
                    ...f,
                    effective_from: e.target.value,
                  }))
                }
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Days on or after this date use the new rate. Days before it keep whatever rate was effective at the time.
              </p>
            </div>

            {rateHistory.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                  Recent Rate Changes
                </div>

                <div className="border rounded-xl overflow-hidden">
                  {rateHistory.slice(0, 6).map((r) => (
                    <div
                      key={r.date}
                      className="flex justify-between px-3 py-1.5 border-t first:border-t-0 text-xs"
                    >
                      <span className="text-slate-500">
                        Effective {r.date}
                      </span>
                      <span className="font-data font-medium">
                        {money(r.rate_per_balla)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {rateHistoryLoading && (
              <div className="text-xs text-slate-400">
                Loading rate history...
              </div>
            )}

          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRateDialogOpen(false)}
            >
              Cancel
            </Button>

            <Button
              onClick={saveDailyRate}
              disabled={rateSaving}
              className="bg-slate-900"
            >
              {rateSaving ? "Saving..." : "Save Rate"}
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>

    </div>
  );
}
